import { useMutation } from '@apollo/client';
import type { UseGetDealInsightsJobId } from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import type { DealInsightsCreateJobIdResponseApi } from '@openx/types/dealInsights.ts';
import { useCallback, useEffect, useState } from 'react';
import {
  CreateDealInsightsJobDocument,
  type CreateDealInsightsJobMutationVariables,
} from '../../../../graphql/mutation/deals/CreateDealInsightsJob.generated.tsx';

export const useGetDealInsightsJobId: UseGetDealInsightsJobId = ({ payload, enabled = true }) => {
  const [state, setState] = useState<{
    data?: DealInsightsCreateJobIdResponseApi;
    isLoading: boolean;
    error?: Error | null;
  }>({ isLoading: true });

  const [createDealInsightsJob, { reset }] = useMutation(CreateDealInsightsJobDocument);

  const payloadString = JSON.stringify(payload);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Rerender loop if payload used as dependency directly
  const createDealInsightsJobWithVariables = useCallback(() => {
    setState({ isLoading: true });

    createDealInsightsJob({
      onCompleted: data => {
        setState(prevState => ({
          ...prevState,
          data: { job_id: data.createDealInsightsJob?.job_id, params: payload },
          isLoading: false,
        }));
      },
      onError: error => {
        setState(prevState => ({ ...prevState, data: undefined, error, isLoading: false }));
      },
      variables: { ...(payload as CreateDealInsightsJobMutationVariables) },
    });
  }, [payloadString, createDealInsightsJob]);

  useEffect(() => {
    if (enabled) {
      createDealInsightsJobWithVariables();
    }
  }, [createDealInsightsJobWithVariables, enabled]);

  const refetch = useCallback(() => {
    reset();
    createDealInsightsJobWithVariables();
  }, [createDealInsightsJobWithVariables, reset]);

  return { ...state, refetch };
};
